import { mapCreatePriceRequestToPriceRequestDto } from '@domains/subscription/priv/Billables/api/mappers'
import {
  useGetFeatures,
  useGetFeaturesForTemplate,
  useOverriddenFeaturesForCustomer,
} from '@domains/subscription/priv/Features/api/queries'
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CreateDefaultConfigFormData, CreatePriceRequest, Eligibility, PricePlanTemplate } from '../types'
import type { CreateTemplateFromCopyPayload } from './api'
import {
  createDefaultConfig,
  createTemplate,
  createTemplateFromExisting,
  createTemplateOptions,
  deleteBillable,
  deleteTemplate,
  getBillables,
  getDefaultConfigs,
  getMigrationStatus,
  getPricesForTemplate,
  getSupportedCurrencies,
  getSupportedPartners,
  getTemplate,
  getTemplateForCustomer,
  getTemplates,
  removeDefaultConfig,
  saveBillablePrice,
  startMigration,
  updateDefaultConfig,
  updateTemplate,
  uploadMigrationFile,
} from './endpoints'

enum queryKeys {
  SUBSCRIPTION = 'subscription',
  TEMPLATES = 'templates',
  FEATURES = 'features',
  BILLABLES = 'billables',
  ELIGIBILITY = 'eligibility',
  DEFAULT_CONFIGS = 'defaultConfigs',
  CURRENCIES = 'currencies',
  PARTNERS = 'partners',
}

export const QueryKey = {
  Template: (id: string) => [queryKeys.SUBSCRIPTION, queryKeys.TEMPLATES, id] as const,
  Templates: () => [queryKeys.SUBSCRIPTION, queryKeys.TEMPLATES] as const,
  Features: () => [queryKeys.SUBSCRIPTION, queryKeys.FEATURES] as const,
  Billables: () => [queryKeys.SUBSCRIPTION, queryKeys.BILLABLES] as const,
  Eligibility: () => [queryKeys.SUBSCRIPTION, queryKeys.ELIGIBILITY] as const,
  Prices: (templateId: string) => [queryKeys.SUBSCRIPTION, queryKeys.BILLABLES, templateId] as const,
  DefaultConfigs: () => [queryKeys.SUBSCRIPTION, queryKeys.DEFAULT_CONFIGS] as const,
  Currencies: () => [queryKeys.SUBSCRIPTION, queryKeys.CURRENCIES] as const,
  Partners: () => [queryKeys.SUBSCRIPTION, queryKeys.PARTNERS] as const,
}

export const useGetTemplates = () =>
  useQuery({
    queryKey: QueryKey.Templates(),
    queryFn: getTemplates,
  })

export const useAvailableBillables = (templateId: string) => {
  const { data: allBillables = [] } = useBillables()
  const { data: templatePrices = [] } = usePricesForTemplate(templateId)

  const availableBillables = allBillables.filter(
    (billable) => !templatePrices.some((templatePrice) => templatePrice.billableId === billable.id),
  )

  return availableBillables
}

export const useAvaliableFeatures = (templateId: string) => {
  const { data: allFeatures = [] } = useGetFeatures()
  const { data: templateFeatures = [] } = useGetFeaturesForTemplate(templateId)

  const availableFeatures = allFeatures.filter((feature) => !templateFeatures.some((templateFeature) => templateFeature.id === feature.id))

  return availableFeatures
}

export const useGetMergedTemplate = (
  templateId: string,
): { data: PricePlanTemplate | undefined; isLoading: boolean; isFetching: boolean } => {
  const { data: template, isLoading: templateIsLoading, isFetching: templateIsFetching } = useGetTemplate(templateId)
  const {
    data: templateFeatures = [],
    isLoading: templateFeaturesIsLoading,
    isFetching: templateFeaturesIsFetching,
  } = useGetFeaturesForTemplate(templateId)
  const {
    data: templateBillables = [],
    isLoading: templateBillablesIsLoading,
    isFetching: templateBillablesIsFetching,
  } = usePricesForTemplate(templateId)

  return {
    data: template ? { ...template, features: templateFeatures, billables: templateBillables } : undefined,
    isLoading: templateIsLoading || templateFeaturesIsLoading || templateBillablesIsLoading,
    isFetching: templateIsFetching || templateFeaturesIsFetching || templateBillablesIsFetching,
  }
}

export const useMergedCustomerTemplateWithFeatures = (customerId: string) => {
  const { data: template, isLoading: templateIsLoading } = useCustomerTemplate(customerId)
  const { data: customerFeatures = [], isLoading: customerFeaturesIsLoading } = useOverriddenFeaturesForCustomer({ customerId })

  return {
    data: template ? { ...template, features: customerFeatures } : undefined,
    isLoading: templateIsLoading || customerFeaturesIsLoading,
  }
}

export const useInactivatedFeatures = (activeFeatureIds: string[] = []) => {
  const { data: allFeatures = [] } = useGetFeatures()

  return allFeatures.filter((feature) => !activeFeatureIds.includes(feature.id))
}

export const useCustomerTemplate = (customerId: string) =>
  useQuery({
    queryKey: [queryKeys.SUBSCRIPTION, queryKeys.TEMPLATES, customerId],
    queryFn: () => getTemplateForCustomer(customerId),
  })

export const invalidateCustomerTemplate = ({ queryClient, customerId }: { queryClient: QueryClient; customerId: string }) => {
  queryClient.invalidateQueries({
    queryKey: [queryKeys.SUBSCRIPTION, queryKeys.TEMPLATES, customerId],
  })
}

export const useGetTemplate = (id: string) =>
  useQuery({
    queryKey: QueryKey.Template(id),
    queryFn: () => getTemplate(id),
  })

export const useCreateTemplate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ name, externalName, eligibility }: { name: string; externalName: string; eligibility: Eligibility }) =>
      createTemplate(name, externalName, eligibility),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QueryKey.Templates(),
      })
    },
  })
}

export const useEditTemplate = (templateId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (options: { name: string; externalName: string; eligibility: Eligibility }) => updateTemplate(templateId, options),
    onSuccess: (data) => {
      queryClient.setQueryData(QueryKey.Template(templateId), data)
      queryClient.invalidateQueries({
        queryKey: QueryKey.Templates(),
      })
      queryClient.invalidateQueries({
        queryKey: QueryKey.Template(data.id),
      })
    },
  })
}

export const useCreateTemplateFromExisting = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: CreateTemplateFromCopyPayload) => createTemplateFromExisting(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.SUBSCRIPTION, queryKeys.TEMPLATES],
      })
    },
  })
}

export const useDeleteTemplate = () => {
  return useMutation({
    mutationFn: (templateId: string) => deleteTemplate(templateId),
  })
}

interface SaveBilliableRequestParams {
  billableId: string
  billablePrice: CreatePriceRequest
}

export const useSaveBillablePrice = (templateId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [queryKeys.SUBSCRIPTION, queryKeys.BILLABLES, templateId],
    mutationFn: ({ billableId, billablePrice }: SaveBilliableRequestParams) => {
      console.log({ billablePrice })
      return saveBillablePrice({
        templateId,
        billableId,
        priceRequest: mapCreatePriceRequestToPriceRequestDto(billablePrice),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QueryKey.Billables(),
      })
    },
  })
}

export const useDeleteBillableItem = (templateId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [queryKeys.SUBSCRIPTION, queryKeys.BILLABLES, templateId],
    mutationFn: (billableId: string) => {
      return deleteBillable({ templateId, billableId })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.SUBSCRIPTION, queryKeys.BILLABLES, templateId],
      })
    },
  })
}

export const useBillables = () =>
  useQuery({
    queryKey: QueryKey.Billables(),
    queryFn: getBillables,
  })

export const usePricesForTemplate = (templateId: string) =>
  useQuery({
    queryKey: QueryKey.Prices(templateId),
    queryFn: () => getPricesForTemplate(templateId),
  })

export const useCreateTemplateOptions = () =>
  useQuery({
    queryKey: [queryKeys.SUBSCRIPTION, queryKeys.ELIGIBILITY],
    queryFn: () => createTemplateOptions(),
  })

export const useGetDefaultConfigs = () =>
  useQuery({
    queryKey: QueryKey.DefaultConfigs(),
    queryFn: getDefaultConfigs,
  })

export const useGetSupportedCurrencies = () =>
  useQuery({
    queryKey: QueryKey.Currencies(),
    queryFn: getSupportedCurrencies,
  })

export const useGetSupportedPartners = () =>
  useQuery({
    queryKey: QueryKey.Partners(),
    queryFn: getSupportedPartners,
  })

export const useCreateDefaultConfig = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateDefaultConfigFormData) => createDefaultConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QueryKey.DefaultConfigs(),
      })
    },
  })
}

export const useUpdateDefaultConfig = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ configId, data }: { configId: string; data: CreateDefaultConfigFormData }) => updateDefaultConfig(configId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QueryKey.DefaultConfigs(),
      })
    },
  })
}

export const useRemoveDefaultConfig = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (configId: string) => removeDefaultConfig(configId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QueryKey.DefaultConfigs(),
      })
    },
  })
}

export const useUploadMigrationFile = () => {
  return useMutation({
    mutationFn: (file: File) => uploadMigrationFile(file),
  })
}

export const useStartMigration = () => {
  return useMutation({
    mutationFn: (fileId: string) => startMigration(fileId),
  })
}

export const useGetMigrationStatus = (fileId: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: [queryKeys.SUBSCRIPTION, 'migration', fileId],
    queryFn: () => getMigrationStatus(fileId!),
    enabled: !!fileId && enabled,
    refetchInterval: (data) => {
      if (data && data.pending === 0) return false
      return 3000
    },
  })
